import { SectionCard } from '@/components/SectionCard'
import { Button } from '@/components/ui/button'
import { Check, Printer, QrCode } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { QRCard } from './QRCard'

import { getScanUrl, printQRCodes, type QRType } from '../utils/qrcode'

export function QRCodeManager<T>({
  data,
  type,
  path,
  getId,
  renderInfo,
  getPrintLabel,
}: {
  data: T[]
  type: QRType
  path?: string
  getId: (item: T) => string
  renderInfo: (item: T) => React.ReactNode
  getPrintLabel?: (item: T) => {
    title: string
    subtitle?: string
  }
}) {
  const { t } = useTranslation()
  const [selected, setSelected] = useState<Set<string>>(new Set())

  function toggle(id: string) {
    setSelected(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  function selectAll() {
    if (selected.size === data.length) {
      setSelected(new Set())
    } else {
      setSelected(new Set(data.map(getId)))
    }
  }

  async function handlePrint() {
    if (selected.size === 0) return

    await printQRCodes({
      items: data
        .filter(item => selected.has(getId(item)))
        .map(item => {
          const id = getId(item)
          const label = getPrintLabel?.(item)

          return {
            url: getScanUrl(type, id, path),
            title: label?.title ?? id,
            subtitle: label?.subtitle,
          }
        }),
    })
  }

  return (
    <SectionCard
      header={
        <div className="text-sm font-medium">
          <QrCode className="mr-2 inline size-4" />
          {t('common.qrcodes')} ({t(`common.${type}`)})
        </div>
      }
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg bg-secondary/30 p-3">
        <div className="min-w-0 flex flex-wrap items-center gap-3">
          <div
            onClick={selectAll}
            className="flex shrink-0 cursor-pointer items-center gap-2 text-xs text-muted-foreground hover:text-foreground"
          >
            <div
              className={`flex size-4 items-center justify-center rounded border ${
                selected.size === data.length && data.length > 0 ? 'border-primary bg-primary' : 'border-border'
              }`}
            >
              {selected.size === data.length && data.length > 0 && <Check className="size-3 text-primary-foreground" />}
            </div>

            {t('common.qrcodeManager.selectAll')}
          </div>

          <span className="truncate text-xs text-muted-foreground">
            {t('common.qrcodeManager.selectedFromAll', {
              count: selected.size,
              all: data.length,
            })}
          </span>
        </div>

        <Button
          size="sm"
          className="h-auto max-w-full justify-center gap-1 px-3 py-2 text-center text-xs"
          onClick={handlePrint}
          disabled={selected.size === 0}
        >
          <Printer className="size-3 shrink-0" />

          <span className="truncate">
            {t('common.qrcodeManager.printQr')} ({selected.size})
          </span>
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
        {data.map(item => {
          const id = getId(item)

          return (
            <QRCard
              key={id}
              id={id}
              url={getScanUrl(type, id, path)}
              selected={selected.has(id)}
              onClick={() => toggle(id)}
              renderInfo={renderInfo(item)}
            />
          )
        })}
      </div>
    </SectionCard>
  )
}
